import type { RestartOrderMode } from '@/components/modals/RestartOrderModal'
import { RestartDraftExistsError } from '@/features/ordering/errors'
import {
  getOrderRestartEligibility,
  getOrderStatusByPatient,
  OrderStatus,
  type OrderRestartEligibility,
} from '@/utils/DatabaseOperations'
import * as Sentry from '@sentry/react'

export interface RestartableCancelledOrder {
  orderId: string
  eligibility: OrderRestartEligibility
}

/**
 * The patient's most recent order when it is cancelled and can be restarted (GEN-6020), else null.
 * Throws when either lookup fails.
 */
export async function findRestartableCancelledOrder(
  patientLookupId: string,
  epicAccessToken: string | null
): Promise<RestartableCancelledOrder | null> {
  const latest = await getOrderStatusByPatient(patientLookupId, 'spa', epicAccessToken)
  if (!latest?.order_id || latest.status?.toLowerCase() !== OrderStatus.CANCELLED) {
    return null
  }
  const eligibility = await getOrderRestartEligibility(latest.order_id)
  return eligibility.eligible ? { orderId: latest.order_id, eligibility } : null
}

export const RESTART_FAILED_MESSAGE = "Couldn't restart the cancelled order. You can continue with a new order."

/** What to do when creating the restarted draft fails: resume the patient's open draft, or tell the provider. */
export function restartFailureOutcome(
  error: unknown
): { kind: 'resumeDraft'; sessionId: string } | { kind: 'showError'; message: string } {
  if (error instanceof RestartDraftExistsError) {
    return { kind: 'resumeDraft', sessionId: error.existingDraftSessionId }
  }
  return { kind: 'showError', message: RESTART_FAILED_MESSAGE }
}

/**
 * Offer to restart the patient's cancelled order and, on a choice, create the restarted draft.
 *
 * Returns the created session, or null when there is nothing to restart, the
 * lookup fails, or the provider dismisses the prompt — the caller then starts a
 * plain new order. Create failures propagate.
 */
export async function offerRestartOfCancelledOrder(params: {
  patientLookupId: string
  epicAccessToken: string | null
  promptForMode: (eligibility: OrderRestartEligibility) => Promise<RestartOrderMode | null>
  createSession: (restart: {
    restartFromOrderId: string
    restartMode: RestartOrderMode
  }) => Promise<Record<string, unknown>>
}): Promise<Record<string, unknown> | null> {
  let restartable: RestartableCancelledOrder | null
  try {
    restartable = await findRestartableCancelledOrder(params.patientLookupId, params.epicAccessToken)
  } catch (e) {
    Sentry.captureException(e, { tags: { operation: 'order.restart.lookup' } })
    return null
  }
  if (!restartable) return null
  const mode = await params.promptForMode(restartable.eligibility)
  if (!mode) return null
  return params.createSession({ restartFromOrderId: restartable.orderId, restartMode: mode })
}
